// /src/treeFinder.js
import { toLonLat } from "ol/proj.js";
import { Circle as CircleGeom, Point } from "ol/geom";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import Feature from "ol/Feature";
import { Style, Stroke, Fill, Circle as CircleStyle } from "ol/style";

import { getActualMap } from "./globalContext.js";
import { removeMapLayer } from "./mapFunctions.js";
import {
  fetchTreesData,
  fetchGeologicalData,
  getLandInfo,
  fetchClimateAverages,
} from "./treeFinderv2Service.js";

import { calculateTreeScores } from "./treeFinderv2CalculateTreeScores.js";
import { renderTreeRecommendation } from "./treeFinderv2RenderRecommendation.js";
import { aiTreeRecommenderService } from "./Services/treeRecommenderService.js";

let AI_TEST_DATEN = [
  {
    art: "Winterlinde (Tilia cordata)",
    eignung_score: 85,
    begr: "Die Winterlinde ist anpassungsfähig an verschiedene Böden und klimatische Bedingungen und bietet guten Schatten.",
    hinweise: "Tolerant gegenüber städtischen Bedingungen",
    pflanzqualitaet: "Hochstamm 16-18 cm, Ballen",
    anforderungen: {
      min_wurzelraum_m3: 15,
      boden_ph: "5.5-7.5",
      pflegehinweis: "Regelmäßiger Schnitt zur Kronenpflege",
    },
  },
  {
    art: "Spitzahorn (Acer platanoides)",
    eignung_score: 80,
    begr: "Spitzahorn ist robust, verträgt städtische Bedingungen gut und bietet dichten Schatten.",
    hinweise: "Mäßig salztolerant",
    pflanzqualitaet: "Hochstamm 14-16 cm, Ballen",
    anforderungen: {
      min_wurzelraum_m3: 12,
      boden_ph: "6.0-7.5",
      pflegehinweis: "Jährlicher Schnitt zur Formgebung",
    },
  },
  {
    art: "Feldahorn (Acer campestre)",
    eignung_score: 78,
    begr: "Feldahorn ist hitze- und trockenheitsresistent und eignet sich gut für kleinere Flächen.",
    hinweise: "Gut für Biodiversität",
    pflanzqualitaet: "Hochstamm 12-14 cm, Ballen",
    anforderungen: {
      min_wurzelraum_m3: 10,
      boden_ph: "6.0-8.0",
      pflegehinweis: "Schnitt zur Förderung der Verzweigung",
    },
  },
  {
    art: "Hainbuche (Carpinus betulus)",
    eignung_score: 82,
    begr: "Hainbuche ist pflegeleicht, verträgt Schatten und ist anpassungsfähig an verschiedene Böden.",
    hinweise: "Gute Windbeständigkeit",
    pflanzqualitaet: "Hochstamm 14-16 cm, Ballen",
    anforderungen: {
      min_wurzelraum_m3: 10,
      boden_ph: "6.0-7.5",
      pflegehinweis: "Regelmäßiger Formschnitt",
    },
  },
  {
    art: "Rotbuche (Fagus sylvatica)",
    eignung_score: 75,
    begr: "Rotbuche bietet dichten Schatten und ist anpassungsfähig, benötigt jedoch ausreichend Platz.",
    hinweise: "Empfindlich gegenüber Salz",
    pflanzqualitaet: "Hochstamm 16-18 cm, Ballen",
    anforderungen: {
      min_wurzelraum_m3: 20,
      boden_ph: "5.0-6.5",
      pflegehinweis: "Schnitt zur Kronenpflege erforderlich",
    },
  },
];

let radius = 100;
let coordinateRadius = null;
const circleLayerName = "Baumberater-Kreis";
const containerEl = document.getElementById("Baumberatercontent");
const datenabruf = document.getElementById("Datenabruf");
const containerAI = document.getElementById("TreefinderAIResult");
const containerAIEl = document.getElementById("TreefinderAIResultContainer");
const slider = document.getElementById("diameterRange");
const valueEl = document.getElementById("diameterValue");

slider.addEventListener("input", onDiameterChange);
slider.addEventListener("change", onDiameterChange);

// Wert bei Bedarf auslesen (z. B. in anderer Funktion)
function getDiameterMeters() {
  return Number(slider.value);
}

function setDiameterMeters(radius) {
  slider.value = radius;
}

// Handler für das 'change'-Event (feuert beim Loslassen/Blur)
function onDiameterChange(evt) {
  const diameter = Number(evt.target.value);

  containerAI.hidden = true;

  // Label aktualisieren (falls vorhanden)
  if (valueEl) valueEl.textContent = diameter;

  // App-spezifische Logik ausführen
  if (coordinateRadius != null) {
    treeFinder(coordinateRadius);
  }
}

export async function treeFinder(coordinate) {
  resetPlausibilityCard();

  const [lon, lat] = toLonLat(coordinate);
  coordinateRadius = coordinate;

  clearRecommendedTrees();
  drawCircle(coordinate);
  containerAI.hidden = true;
  datenabruf.hidden = false;

  let treesInArea = {};

  // Frage Kataster ab
  try {
    setStatus("status-kataster", renderLoading());
    treesInArea = await fetchTreesData(coordinate, getDiameterMeters());
    if (!Array.isArray(treesInArea) || treesInArea.length === 0) {
      setStatus("status-kataster", renderError(treesInArea.length + " Bäume"));
    } else if (!Array.isArray(treesInArea) || treesInArea.length < 10) {
      setStatus(
        "status-kataster",
        renderWarning(treesInArea.length + " Bäume")
      );
    } else if (!Array.isArray(treesInArea) || treesInArea.length >= 10) {
      setStatus(
        "status-kataster",
        renderSuccess(treesInArea.length + " Bäume")
      );
    }
  } catch (err) {
    setStatus("status-kataster", renderError());
    console.error(`Fehler in Kataster:` + err);
  }

  const geology = await withTimeout(getLandInfo, [lat, lon], "status-building");

  // PRÜFE ERGEBNIS
  showPlausibilityCard();
  updatePlausibilityStatus(geology);

  // Frage Schnittstellen ab ab
  const [climate, isBuilt] = await Promise.all([
    withTimeout(fetchGeologicalData, [lat, lon], "status-geology"),
    withTimeout(fetchClimateAverages, [lat, lon], "status-climate"),
  ]);

  // Baumumfeld Toplist
  const Baumumfeld = buildBaumumfeldToplist(treesInArea, 3);
  const vitalityRecommendation = {
    KatasterdatenToplist: Baumumfeld.Baumumfeld,
  };
  // Baue String für KI zusammen
  const aiTreeRecommendationData = {};
  Object.assign(
    aiTreeRecommendationData,
    vitalityRecommendation,
    geology,
    climate,
    isBuilt
  ); // state wird verändert

  let AIResult = null;
  AIResult = await withTimeout(
    aiTreeRecommenderService,
    [lat, lon, aiTreeRecommendationData], // komplette Signatur in Reihenfolge
    "status-ai",
    { timeoutMs: 15000 } // forwardExtra kann weg
  );

  // AIResult = AI_TEST_DATEN;

  if (AIResult != null) {
    AIResult = sortiereNachScoreAbsteigend(AIResult);

    // Rendern:
    renderBaumkarten(AIResult, containerAIEl);
  }

  /*Errechne Score und Grafik
  const scoredRecommendations = calculateTreeScores({
    geology,
    climate,
    isBuilt,
    treesInArea,
  });

  if (scoredRecommendations.length > 0) {
    const top5 = scoredRecommendations.slice(0, 5).map((t) => ({
      Baumart: t.name,
      Vitalitaet: t.vitalitaet,
      Anzahl: t.anzahl,
      Score: t.score,
      Breakdown: t.breakdown,
    }));
    renderTreeRecommendation(containerEl, treesInArea.length, top5);
  }*/
}

function sortiereNachScoreAbsteigend(array) {
  return array.slice().sort((a, b) => b.eignung_score - a.eignung_score);
}

function clearRecommendedTrees() {
  containerEl.innerHTML = "";
  containerAIEl.innerHTML = "";
}
function showNoTreesFound() {
  containerEl.innerHTML =
    "Keine Bäume im Umkreis von " + radius + "m gefunden.";
}

function drawCircle(coordinate) {
  const map = getActualMap().globalMap;
  removeMapLayer(map, circleLayerName);

  const circleSource = new VectorSource();
  const circleLayer = new VectorLayer({
    title: circleLayerName,
    name: circleLayerName,
    source: circleSource,
    style: new Style({
      stroke: new Stroke({ color: "blue", width: 2 }),
      fill: new Fill({ color: "rgba(0, 0, 255, 0.1)" }),
    }),
  });

  // Kreis (OL erwartet Radius, nicht Durchmesser)
  const radius = getDiameterMeters() / 2;
  const circle = new CircleGeom(coordinate, radius);
  const circleFeature = new Feature(circle);

  circleSource.clear();
  circleSource.addFeature(circleFeature);

  // Roter Punkt in der Mitte
  const pointFeature = new Feature(new Point(coordinate));
  pointFeature.setStyle(
    new Style({
      image: new CircleStyle({
        radius: 3,
        fill: new Fill({ color: "red" }),
        stroke: new Stroke({ color: "white", width: 1 }),
      }),
      zIndex: 10, // bleibt über dem Kreis sichtbar
    })
  );
  circleSource.addFeature(pointFeature);

  map.addLayer(circleLayer);
}

export function zuruecksetzenBaumberater() {
  const map = getActualMap().globalMap;
  removeMapLayer(map, circleLayerName);
  clearRecommendedTrees();
  containerAI.hidden = true;
  datenabruf.hidden = true;
  coordinateRadius = null;
}

function renderLoading() {
  return `<div class="spinner-border spinner-border-sm text-primary" role="status"></div>`;
}
function renderSuccess(msg = "") {
  return `
  ${msg ? `<span class="status-msg">${msg}</span>` : ""}  
  <i class="bi bi-check-circle-fill text-success"></i>
  `;
}

function renderError(msg = "") {
  return `
    ${msg ? `<span class="status-msg">${msg}</span>` : ""} 
    <i class="bi bi-x-circle-fill text-danger"></i>
  `;
}

function renderWarning(msg = "") {
  return `
    ${msg ? `<span class="status-msg">${msg}</span>` : ""}
    <i class="bi bi-exclamation-triangle-fill text-warning"></i>
  `;
}

function setStatus(id, html) {
  document.getElementById(id).innerHTML = html;
}

/**
 * Führt eine async Funktion aus, zeigt Ladeicon/Erfolg/Fehler,
 * optionales Timeout und optionale Zusatzdaten.
 *
 * @param {Function} fn        - Ziel-Funktion (kann async sein)
 * @param {Array}    args      - Argumente für fn (ohne Zusatzdaten)
 * @param {string}   statusId  - ID für Status-UI
 * @param {object}   options   - optionale Einstellungen
 *   - timeoutMs: number       - Default 40000
 *   - extra: any              - optionale Zusatzdaten
 *   - forwardExtra: boolean   - extra an fn als letztes Argument anhängen
 */
async function withTimeout(fn, args = [], statusId, options = {}) {
  const { timeoutMs = 40000, extra, forwardExtra = false } = options;

  setStatus(statusId, renderLoading());

  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("timeout")), timeoutMs)
  );

  // falls gewünscht, extra an die fn-Aufrufargs anhängen
  const callArgs =
    forwardExtra && extra !== undefined ? [...args, extra] : args;

  try {
    const result = await Promise.race([fn(...callArgs), timeout]);
    setStatus(statusId, renderSuccess());
    return result;
  } catch (err) {
    setStatus(statusId, renderError());
    console.error(`Fehler in ${statusId}:`, err);
    return null;
  }
}

/**
 * Baut eine Toplist (Top N) der Baumarten nach Vitalität und Häufigkeit
 * und schreibt die Gewinner unter data.Baumumfeld.
 * Zusätzlich bekommt jedes Feature ein verschachteltes Feld `Baumumfeld`
 * mit { Baumart, Vitalitaet }.
 *
 * Sortierlogik:
 *   1) Vitalität (Zahl im String) absteigend = höhere Zahl zuerst
 *   2) Vorkommen der Baumart absteigend
 *   3) Baumart-Name alphabetisch
 *
 * @param {object} data - Dein großes JSON-Objekt (Features + Meta)
 * @param {number} topN - Anzahl Top-Einträge (Default 3)
 * @returns {object}    - Neues Objekt mit Toplist unter data.Baumumfeld
 */
function buildBaumumfeldToplist(data, topN = 3) {
  // Alle Feature-Objekte sammeln
  const entries = Object.entries(data);
  const features = entries
    .filter(([, v]) => v && v.type === "Feature" && v.properties)
    .map(([, v]) => v);

  // Gruppieren nach Baumart und beste (höchste) Vitalität merken
  const groups = new Map();
  for (const f of features) {
    const baumart = f.properties.baumart ?? "Unbekannt";
    const vitStr = f.properties.vitalitaet ?? "";
    const match = String(vitStr).match(/-?\d+(?:\.\d+)?/); // Zahl aus "1 (…)" extrahieren
    const vitNum = match ? Number(match[0]) : Number.NEGATIVE_INFINITY;

    const g = groups.get(baumart) || {
      count: 0,
      bestNum: Number.NEGATIVE_INFINITY,
      bestStr: null,
    };
    g.count++;
    // Wenn "niedrigere Zahl = bessere Vitalität" gelten soll,
    // tausche hier das ">" gegen "<" aus:
    if (vitNum > g.bestNum) {
      g.bestNum = vitNum;
      g.bestStr = vitStr;
    }
    groups.set(baumart, g);
  }

  // Toplist bauen und sortieren
  const toplist = [...groups.entries()]
    .map(([baumart, g]) => ({
      baumart,
      vitalitaetNum: g.bestNum,
      vitalitaet: g.bestStr,
      vorkommen: g.count,
    }))
    .sort(
      (a, b) =>
        b.vitalitaetNum - a.vitalitaetNum || // ggf. Vorzeichen drehen (s.o.)
        b.vorkommen - a.vorkommen ||
        a.baumart.localeCompare(b.baumart)
    )
    .slice(0, topN);

  // Gewinner-Objekt unter "Baumumfeld" fürs Root bauen
  const winnersObject = Object.fromEntries(
    toplist.map((e, i) => [
      String(i + 1),
      { Baumart: e.baumart, Vitalitaet: e.vitalitaet },
    ])
  );

  // Neues Objekt zurückgeben:
  // - jedes Feature mit verschachteltem { Baumumfeld: { Baumart, Vitalitaet } }
  // - Root bekommt Baumumfeld = Gewinnerliste
  const updated = Object.fromEntries(
    entries.map(([k, v]) => {
      if (v && v.type === "Feature" && v.properties) {
        return [
          k,
          {
            ...v,
            Baumumfeld: {
              Baumart: v.properties.baumart ?? null,
              Vitalitaet: v.properties.vitalitaet ?? null,
            },
          },
        ];
      }
      return [k, v];
    })
  );

  return { ...updated, Baumumfeld: winnersObject };
}

/**
 * Rendert Baumkarten in einen Container.
 * - Empfehlung + Score sofort sichtbar
 * - "Weitere Infos" (Begründung, Hinweise, Anforderungen) per Collapse
 * - Button toggelt öffnen/schließen und ändert dynamisch das Label
 *
 * @param {Array<Object>} data - Dein JSON-Array.
 * @param {HTMLElement|string} container - DOM-Element oder Element-ID.
 */
function renderBaumkarten(data, container) {
  if (typeof container === "string")
    container = document.getElementById(container);
  if (!container) throw new Error("Container nicht gefunden");

  container.innerHTML = "";

  const recFor = (score) => {
    if (score >= 85)
      return {
        text: "Empfehlung: hoch",
        badge: "bg-success",
        bar: "bg-success",
      };
    if (score >= 70)
      return {
        text: "Empfehlung: mittel",
        badge: "bg-warning text-dark",
        bar: "bg-warning",
      };
    return {
      text: "Empfehlung: niedrig",
      badge: "bg-danger",
      bar: "bg-danger",
    };
  };

  containerAI.hidden = false;

  data.forEach((item, idx) => {
    const score = Number(item.eignung_score) || 0;
    const rec = recFor(score);
    const uid = `${Date.now()}-${idx}`;
    const collapseId = `details-${uid}`;
    const infoId = `info-${uid}`;

    const card = document.createElement("div");
    card.className = "compact-card shadow-sm";

    card.innerHTML = `
        <div class="card-header bg-white">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div class="me-2">
              <div class="fw-bold">${item.art ?? ""}</div>
  
              <!-- Badge + Progressbar + Score + Info-Icon -->
              <div class="d-flex align-items-center gap-2 mt-1">
                <span class="badge rounded-pill ${rec.badge}">${rec.text}</span>
  
                <div class="d-flex align-items-center gap-1">
                  <div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar ${
                      rec.bar
                    }" style="width:${score}%;" aria-valuenow="${score}"></div>
                  </div>
                  <span class="fw-bold" style="min-width:2ch;">${score}</span>
  
                  <!-- Info-Icon OHNE data-bs-Attribute -->
                  <a id="${infoId}" class="text-muted ms-1" href="#" title="Weitere Infos"
                     aria-controls="${collapseId}" aria-expanded="false" role="button">
                    <i class="bi bi-question-circle"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Collapse-Bereich -->
        <div class="collapse" id="${collapseId}">
          <div class="border rounded p-3 mt-2">
            ${
              item.begr
                ? `
              <h6 class="mb-1">Begründung</h6>
              <p class="mb-2">${item.begr}</p>`
                : ""
            }
            ${
              item.hinweise
                ? `
              <h6 class="mb-1">Hinweise</h6>
              <p class="mb-2">${item.hinweise}</p>`
                : ""
            }
  
            <h6 class="mb-2">Anforderungen</h6>
            <div class="row gy-2">
              <div class="col-12">
                <div class="text-muted small">Mindest-Wurzelraum</div>
                <div class="fw-semibold">${
                  item.anforderungen?.min_wurzelraum_m3 ?? "-"
                } m³</div>
              </div>
              <div class="col-12">
                <div class="text-muted small">Boden-pH</div>
                <div class="fw-semibold">${
                  item.anforderungen?.boden_ph ?? "-"
                }</div>
              </div>
              <div class="col-12">
                <div class="text-muted small">Pflegehinweis</div>
                <div class="fw-semibold">${
                  item.anforderungen?.pflegehinweis ?? "-"
                }</div>
              </div>
            </div>
          </div>
        </div>
    `;

    container.appendChild(card);

    // ✅ Einziger, kontrollierter Toggle
    const info = card.querySelector("#" + infoId);
    const collEl = card.querySelector("#" + collapseId);

    // holt existierende Instanz oder erstellt eine (toggle:false verhindert Autostart)
    const bsColl = bootstrap.Collapse.getOrCreateInstance(collEl, {
      toggle: false,
    });

    info.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation(); // verhindert doppelte Toggles durch Eltern-Handler
      bsColl.toggle();
    });

    // ARIA und ggf. Icon-State syncen
    collEl.addEventListener("shown.bs.collapse", () =>
      info.setAttribute("aria-expanded", "true")
    );
    collEl.addEventListener("hidden.bs.collapse", () =>
      info.setAttribute("aria-expanded", "false")
    );
  });
}

/**
 * Zeigt die Plausibilitäts-Card an
 */
function showPlausibilityCard() {
  const el = document.getElementById("Plausibilitaet");
  el.style.transition = "opacity 0.3s";
  el.style.opacity = "0";
  el.style.display = "";
  requestAnimationFrame(() => { el.style.opacity = "1"; });
  document.getElementById("plausibility-status").innerHTML =
    '<i class="bi bi-hourglass-split text-secondary"></i>';
  document.getElementById("plausibility-message").style.display = "none";
}

/**
 * Versteckt die Plausibilitäts-Card
 */
function hidePlausibilityCard() {
  const el = document.getElementById("Plausibilitaet");
  el.style.transition = "opacity 0.3s";
  el.style.opacity = "0";
  setTimeout(() => { el.style.display = "none"; el.style.opacity = ""; }, 300);
}

/**
 * Aktualisiert die Ampel basierend auf Ergebnis
 * @param {Object} result - Ergebnis von getLandInfo()
 */
function updatePlausibilityStatus(result) {
  const statusEl = document.getElementById("plausibility-status");
  const alertEl = document.getElementById("plausibility-alert");
  const textEl = document.getElementById("plausibility-text");
  const messageEl = document.getElementById("plausibility-message");

  if (!result.success) {
    // GELB - Nicht geeignet (früher ROT)
    statusEl.innerHTML = '<i class="bi bi-exclamation-triangle-fill text-warning"></i>';
    alertEl.classList.remove("alert-danger", "alert-success");
    alertEl.classList.add("alert-warning");

    let detailText = result.error + " Das Ergebnis ist eine Empfehlung für das nähere Umfeld. ";
    if (result.Plausibilitaet.grund) {
      detailText += `<br><small class="text-muted">OSM-Tag: ${result.Plausibilitaet.grund}</small>`;
    }
    textEl.innerHTML = detailText;
    messageEl.style.transition = "opacity 0.3s";
    messageEl.style.opacity = "0";
    messageEl.style.display = "";
    requestAnimationFrame(() => { messageEl.style.opacity = "1"; });

    // Optional: In Console die vollständigen Tags ausgeben
  } else if (result.Plausibilitaet.status === "warnung") {
    // GELB - Warnung
    statusEl.innerHTML = '<i class="bi bi-exclamation-triangle-fill text-warning"></i>';
    alertEl.classList.remove("alert-danger", "alert-success");
    alertEl.classList.add("alert-warning");

    let detailText = result.Plausibilitaet.warnung;
    if (result.Plausibilitaet.grund) {
      detailText += `<br><small class="text-muted">OSM-Tag: ${result.Plausibilitaet.grund}</small>`;
    }
    textEl.innerHTML = detailText;
    messageEl.style.transition = "opacity 0.3s";
    messageEl.style.opacity = "0";
    messageEl.style.display = "";
    requestAnimationFrame(() => { messageEl.style.opacity = "1"; });
  } else {
    // GRÜN - Geeignet
    statusEl.innerHTML = '<i class="bi bi-check-circle-fill text-success"></i>';
    messageEl.style.display = "none";
  }
}

/**
 * Setzt die Plausibilitäts-Card zurück (lädt...)
 */
function resetPlausibilityCard() {
  document.getElementById("plausibility-status").innerHTML =
    '<i class="bi bi-hourglass-split text-secondary"></i>';
  document.getElementById("plausibility-message").style.display = "none";
  document.getElementById("plausibility-text").textContent = "";
  const alertEl = document.getElementById("plausibility-alert");
  alertEl.classList.remove("alert-danger", "alert-warning", "alert-success");
}
